import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import Navigation from '../components/Navigation.jsx'
import Footer from '../components/Footer.jsx'
import { accountApi } from '../services/api.client'

export default function AccountPage() {
    const navigate = useNavigate()
    const [activeTab, setActiveTab] = useState('profile')
    const [profile, setProfile] = useState(null)
    const [searches, setSearches] = useState([])
    const [likedPlaces, setLikedPlaces] = useState([])
    const [likedEvents, setLikedEvents] = useState([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState('')

    useEffect(() => {
        loadAccountData()
    }, [])

    async function loadAccountData() {
        setLoading(true)
        setError('')

        try {
            const [profileRes, searchesRes, placesRes, eventsRes] = await Promise.all([
                accountApi.getProfile(),
                accountApi.getSearchHistory(20),
                accountApi.getLikedPlaces(20),
                accountApi.getLikedEvents(20)
            ])

            setProfile(profileRes.data)
            setSearches(searchesRes.data.searches || [])
            setLikedPlaces(placesRes.data.places || [])
            setLikedEvents(eventsRes.data.events || [])
        } catch (err) {
            setError(err.response?.data?.message || err.message || 'Failed to load account data')
        } finally {
            setLoading(false)
        }
    }

    async function handleDeleteSearch(searchId) {
        try {
            await accountApi.deleteSearch(searchId)
            setSearches(prev => prev.filter(s => s.searchId !== searchId))
        } catch (err) {
            alert('Failed to delete search: ' + (err.response?.data?.message || err.message))
        }
    }

    async function handleUnlikePlace(placeId) {
        try {
            await accountApi.unlikePlace(placeId)
            setLikedPlaces(prev => prev.filter(p => p.id !== placeId))
        } catch (err) {
            alert('Failed to unlike place: ' + (err.response?.data?.message || err.message))
        }
    }

    async function handleUnlikeEvent(eventId) {
        try {
            await accountApi.unlikeEvent(eventId)
            setLikedEvents(prev => prev.filter(e => e.id !== eventId))
        } catch (err) {
            alert('Failed to unlike event: ' + (err.response?.data?.message || err.message))
        }
    }

    function handleSearchClick(search) {
        // Navigate to home page with search parameters
        const date = new Date(search.searchDate)
        const dateStr = date.toISOString().split('T')[0]
        navigate(`/?date=${dateStr}`)
    }

    function handleItemClick(type, id) {
        navigate(`/${type}/${id}`)
    }

    if (loading) {
        return (
            <div className="min-h-screen flex flex-col">
                <Navigation />
                <main className="flex-1 flex items-center justify-center">
                    <div className="text-center">
                        <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-primary border-r-transparent"></div>
                        <p className="mt-4 text-gray-600">Loading account...</p>
                    </div>
                </main>
                <Footer />
            </div>
        )
    }

    if (error) {
        return (
            <div className="min-h-screen flex flex-col">
                <Navigation />
                <main className="flex-1 flex items-center justify-center">
                    <div className="text-center max-w-md mx-auto px-4">
                        <div className="text-red-500 text-5xl mb-4">⚠</div>
                        <h1 className="text-2xl font-bold mb-2">Error Loading Account</h1>
                        <p className="text-gray-600 mb-4">{error}</p>
                        <button
                            onClick={loadAccountData}
                            className="bg-primary text-white px-6 py-2 rounded-lg hover:bg-primary/90"
                        >
                            Try Again
                        </button>
                    </div>
                </main>
                <Footer />
            </div>
        )
    }

    return (
        <div className="min-h-screen flex flex-col">
            <Navigation />

            <main className="flex-1 bg-gray-50 py-8">
                <div className="max-w-6xl mx-auto px-4">
                    {/* Page Header */}
                    <div className="mb-8">
                        <h1 className="text-3xl font-bold text-gray-900">My Account</h1>
                        <p className="text-gray-600 mt-1">Manage your profile, searches, and saved items</p>
                    </div>

                    {/* Tabs */}
                    <div className="bg-white rounded-lg shadow-sm mb-6">
                        <div className="border-b border-gray-200">
                            <nav className="flex -mb-px">
                                <button
                                    onClick={() => setActiveTab('profile')}
                                    className={`py-4 px-6 text-sm font-medium border-b-2 transition-colors ${
                                        activeTab === 'profile'
                                            ? 'border-primary text-primary'
                                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                    }`}
                                >
                                    Profile
                                </button>
                                <button
                                    onClick={() => setActiveTab('searches')}
                                    className={`py-4 px-6 text-sm font-medium border-b-2 transition-colors ${
                                        activeTab === 'searches'
                                            ? 'border-primary text-primary'
                                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                    }`}
                                >
                                    Search History ({searches.length})
                                </button>
                                <button
                                    onClick={() => setActiveTab('places')}
                                    className={`py-4 px-6 text-sm font-medium border-b-2 transition-colors ${
                                        activeTab === 'places'
                                            ? 'border-primary text-primary'
                                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                    }`}
                                >
                                    Liked Places ({likedPlaces.length})
                                </button>
                                <button
                                    onClick={() => setActiveTab('events')}
                                    className={`py-4 px-6 text-sm font-medium border-b-2 transition-colors ${
                                        activeTab === 'events'
                                            ? 'border-primary text-primary'
                                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                    }`}
                                >
                                    Liked Events ({likedEvents.length})
                                </button>
                            </nav>
                        </div>

                        <div className="p-6">
                            {/* Profile Tab */}
                            {activeTab === 'profile' && (
                                <ProfileSection profile={profile} onReload={loadAccountData} />
                            )}

                            {/* Search History Tab */}
                            {activeTab === 'searches' && (
                                <SearchHistorySection
                                    searches={searches}
                                    onDelete={handleDeleteSearch}
                                    onSearchClick={handleSearchClick}
                                />
                            )}

                            {/* Liked Places Tab */}
                            {activeTab === 'places' && (
                                <LikedPlacesSection
                                    places={likedPlaces}
                                    onUnlike={handleUnlikePlace}
                                    onPlaceClick={(id) => handleItemClick('place', id)}
                                />
                            )}

                            {/* Liked Events Tab */}
                            {activeTab === 'events' && (
                                <LikedEventsSection
                                    events={likedEvents}
                                    onUnlike={handleUnlikeEvent}
                                    onEventClick={(id) => handleItemClick('event', id)}
                                />
                            )}
                        </div>
                    </div>
                </div>
            </main>

            <Footer />
        </div>
    )
}

function ProfileSection({ profile, onReload }) {
    if (!profile) return null

    const joinedDate = new Date(profile.joinedDate).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
    })

    return (
        <div className="space-y-6">
            {/* Profile Header */}
            <div className="flex items-start gap-6">
                <div className="w-24 h-24 rounded-full bg-gradient-to-br from-primary to-primary/60 flex items-center justify-center text-white text-3xl font-bold flex-shrink-0">
                    {profile.displayName?.charAt(0).toUpperCase() || 'U'}
                </div>
                <div className="flex-1">
                    <h2 className="text-2xl font-bold text-gray-900">{profile.displayName}</h2>
                    <p className="text-gray-600">{profile.email}</p>
                    <p className="text-sm text-gray-500 mt-1">Member since {joinedDate}</p>
                </div>
            </div>

            {/* Profile Details */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-6 border-t border-gray-200">
                <div>
                    <h3 className="text-sm font-medium text-gray-500 mb-1">Name</h3>
                    <p className="text-gray-900">{profile.firstName} {profile.lastName}</p>
                </div>

                {profile.location && (
                    <div>
                        <h3 className="text-sm font-medium text-gray-500 mb-1">Location</h3>
                        <p className="text-gray-900">
                            {profile.location.city}, {profile.location.state}
                        </p>
                    </div>
                )}

                {profile.preferences?.searchRadius && (
                    <div>
                        <h3 className="text-sm font-medium text-gray-500 mb-1">Search Radius</h3>
                        <p className="text-gray-900">{profile.preferences.searchRadius} miles</p>
                    </div>
                )}

                {profile.preferences?.favoriteCategories && profile.preferences.favoriteCategories.length > 0 && (
                    <div>
                        <h3 className="text-sm font-medium text-gray-500 mb-1">Favorite Categories</h3>
                        <div className="flex flex-wrap gap-2 mt-2">
                            {profile.preferences.favoriteCategories.map(cat => (
                                <span key={cat} className="px-3 py-1 bg-primary/10 text-primary rounded-full text-sm">
                                    {cat}
                                </span>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </div>
    )
}

function SearchHistorySection({ searches, onDelete, onSearchClick }) {
    if (searches.length === 0) {
        return (
            <div className="text-center py-12">
                <div className="text-gray-400 text-5xl mb-4">🔍</div>
                <p className="text-gray-600">No search history yet</p>
                <p className="text-sm text-gray-500 mt-1">Your past searches will appear here</p>
            </div>
        )
    }

    return (
        <div className="space-y-3">
            {searches.map(search => (
                <div
                    key={search.searchId}
                    className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                >
                    <div
                        className="flex-1 cursor-pointer"
                        onClick={() => onSearchClick(search)}
                    >
                        <div className="flex items-center gap-3">
                            <div className="text-gray-400">📅</div>
                            <div>
                                <p className="font-medium text-gray-900">
                                    {new Date(search.searchDate).toLocaleDateString('en-US', {
                                        weekday: 'short',
                                        month: 'short',
                                        day: 'numeric',
                                        year: 'numeric'
                                    })}
                                </p>
                                <p className="text-sm text-gray-600">
                                    {search.location?.displayName || 'Unknown location'} •
                                    {' '}{search.resultsCount?.events || 0} events, {search.resultsCount?.places || 0} places
                                </p>
                                <p className="text-xs text-gray-500 mt-1">
                                    Searched {new Date(search.timestamp).toLocaleDateString()}
                                </p>
                            </div>
                        </div>
                    </div>
                    <button
                        onClick={() => onDelete(search.searchId)}
                        className="ml-4 text-gray-400 hover:text-red-500 transition-colors"
                        title="Delete search"
                    >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                    </button>
                </div>
            ))}
        </div>
    )
}

function LikedPlacesSection({ places, onUnlike, onPlaceClick }) {
    if (places.length === 0) {
        return (
            <div className="text-center py-12">
                <div className="text-gray-400 text-5xl mb-4">📍</div>
                <p className="text-gray-600">No liked places yet</p>
                <p className="text-sm text-gray-500 mt-1">Places you like will appear here</p>
            </div>
        )
    }

    return (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {places.map(place => (
                <div
                    key={place.id}
                    className="bg-white border border-gray-200 rounded-lg overflow-hidden hover:shadow-md transition-shadow"
                >
                    <div
                        className="cursor-pointer"
                        onClick={() => onPlaceClick(place.id)}
                    >
                        <div className="aspect-video bg-gray-200 relative">
                            {place.image ? (
                                <img
                                    src={place.image}
                                    alt={place.name}
                                    className="w-full h-full object-cover"
                                />
                            ) : (
                                <div className="w-full h-full flex items-center justify-center text-gray-400 text-4xl">
                                    📍
                                </div>
                            )}
                            {place.category && (
                                <span className="absolute top-2 right-2 px-2 py-1 bg-white/90 backdrop-blur-sm rounded text-xs font-medium text-gray-700">
                                    {place.category}
                                </span>
                            )}
                        </div>
                        <div className="p-4">
                            <h3 className="font-medium text-gray-900 mb-1">{place.name}</h3>
                            <p className="text-sm text-gray-600 line-clamp-2 mb-2">{place.description}</p>
                            <p className="text-xs text-gray-500">{place.address}</p>
                        </div>
                    </div>
                    <div className="px-4 pb-4">
                        <button
                            onClick={() => onUnlike(place.id)}
                            className="w-full py-2 text-sm text-red-600 hover:bg-red-50 rounded transition-colors"
                        >
                            Remove from Likes
                        </button>
                    </div>
                </div>
            ))}
        </div>
    )
}

function LikedEventsSection({ events, onUnlike, onEventClick }) {
    if (events.length === 0) {
        return (
            <div className="text-center py-12">
                <div className="text-gray-400 text-5xl mb-4">🎉</div>
                <p className="text-gray-600">No liked events yet</p>
                <p className="text-sm text-gray-500 mt-1">Events you like will appear here</p>
            </div>
        )
    }

    return (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {events.map(event => (
                <div
                    key={event.id}
                    className="bg-white border border-gray-200 rounded-lg overflow-hidden hover:shadow-md transition-shadow"
                >
                    <div
                        className="cursor-pointer"
                        onClick={() => onEventClick(event.id)}
                    >
                        <div className="aspect-video bg-gray-200 relative">
                            {event.image ? (
                                <img
                                    src={event.image}
                                    alt={event.name}
                                    className="w-full h-full object-cover"
                                />
                            ) : (
                                <div className="w-full h-full flex items-center justify-center text-gray-400 text-4xl">
                                    🎉
                                </div>
                            )}
                            {event.category && (
                                <span className="absolute top-2 right-2 px-2 py-1 bg-white/90 backdrop-blur-sm rounded text-xs font-medium text-gray-700">
                                    {event.category}
                                </span>
                            )}
                            {event.isExpired && (
                                <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                                    <span className="px-3 py-1 bg-white/90 rounded text-sm font-medium text-gray-700">
                                        Past Event
                                    </span>
                                </div>
                            )}
                        </div>
                        <div className="p-4">
                            <h3 className="font-medium text-gray-900 mb-1">{event.name}</h3>
                            <p className="text-sm text-gray-600 line-clamp-2 mb-2">{event.description}</p>
                            <div className="text-xs text-gray-500 space-y-1">
                                <p>📅 {new Date(event.date).toLocaleDateString('en-US', {
                                    weekday: 'short',
                                    month: 'short',
                                    day: 'numeric',
                                    hour: 'numeric',
                                    minute: '2-digit'
                                })}</p>
                                <p>📍 {event.venue}</p>
                            </div>
                        </div>
                    </div>
                    <div className="px-4 pb-4">
                        <button
                            onClick={() => onUnlike(event.id)}
                            className="w-full py-2 text-sm text-red-600 hover:bg-red-50 rounded transition-colors"
                        >
                            Remove from Likes
                        </button>
                    </div>
                </div>
            ))}
        </div>
    )
}
